import { useEffect } from 'react';
import { create } from 'zustand';

import { urqlClient } from '@shared/api';
import { SILENT_ERRORS_CONTEXT } from '@shared/api/silent-errors';

import { onResult } from './channel';
import { fromGraphqlService } from './connect-flow';
import { INTEGRATION_CONNECTIONS } from './graphql/queries';
import type { ConnectionView, IntegrationConnectionsQuery } from './types';

// The user's connections, as the open cards of a view read them: one shared
// `connections(filter: ALL)` query, read while an open card is mounted, again on a Connect event
// or when the page becomes visible, and never polled (every read asks connectors for the live
// status of each connected service).

interface ConnectionsState {
  /** The last read, or null before one succeeded. */
  views: ConnectionView[] | null;
  /** The last read failed. */
  failed: boolean;
}

export const useConnectionsStore = create<ConnectionsState>(() => ({ views: null, failed: false }));

type Row = NonNullable<
  NonNullable<IntegrationConnectionsQuery['investbanq']>['integrations']
>['connections'][number];

function toView(row: Row): ConnectionView {
  return {
    service: fromGraphqlService(row.service),
    entitled: row.entitled,
    connected: row.connected,
    status: row.status,
    reason: row.reason,
    statusChangedAt: row.statusChangedAt,
    display: row.display,
    connector: {
      name: row.connector.name,
      provider: row.connector.provider,
      providerName: row.connector.providerName,
      description: row.connector.description,
      logo: { key: row.connector.logo.key, brandColor: row.connector.logo.brandColor },
    },
  };
}

async function query(): Promise<ConnectionView[] | null> {
  try {
    const result = await urqlClient
      .query(
        INTEGRATION_CONNECTIONS,
        {},
        { requestPolicy: 'network-only', ...SILENT_ERRORS_CONTEXT },
      )
      .toPromise();
    const rows = result.data?.investbanq?.integrations?.connections;
    if (!result.error && Array.isArray(rows)) {
      const views = rows.map(toView);
      useConnectionsStore.setState({ views, failed: false });
      return views;
    }
  } catch {
    // A read that throws says nothing, as one that failed.
  }
  useConnectionsStore.setState({ failed: true });
  return null;
}

let current: Promise<ConnectionView[] | null> | null = null;
let next: Promise<ConnectionView[] | null> | null = null;

/**
 * Reads the connections. A call made while a read is out waits for it and reads again, so no
 * answer is older than its call (a Connect's `ok` never reads what was true before it); the calls
 * made meanwhile share that next read.
 */
export function readConnections(): Promise<ConnectionView[] | null> {
  if (!current) {
    current = query().finally(() => {
      current = null;
    });
    return current;
  }
  next ??= current.then(() => {
    next = null;
    return readConnections();
  });
  return next;
}

/** The view of one service in a read, by its lowercase id. */
export function viewOf(
  views: ConnectionView[] | null | undefined,
  service: string,
): ConnectionView | undefined {
  return views?.find((v) => v.service === service);
}

let users = 0;
let detach: (() => void) | null = null;

function attach(): () => void {
  const stopHearing = onResult(() => {
    void readConnections();
  });
  const onVisible = () => {
    if (document.visibilityState === 'visible') void readConnections();
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    stopHearing();
    document.removeEventListener('visibilitychange', onVisible);
  };
}

/**
 * The shared connections of a view, read while `enabled` (an open card is mounted) and skipped
 * otherwise.
 */
export function useConnections(enabled: boolean): {
  views: ConnectionView[] | null;
  failed: boolean;
  refresh: () => void;
} {
  const views = useConnectionsStore((s) => s.views);
  const failed = useConnectionsStore((s) => s.failed);
  useEffect(() => {
    if (!enabled) return;
    users += 1;
    if (users === 1) {
      detach = attach();
      void readConnections();
    }
    return () => {
      users -= 1;
      if (users === 0) {
        detach?.();
        detach = null;
      }
    };
  }, [enabled]);
  return {
    views: enabled ? views : null,
    failed: enabled && failed,
    refresh: () => {
      void readConnections();
    },
  };
}
